import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, ViewEncapsulation } from '@angular/core';
import { IFormCondition, IFormQuestion } from '@fi-shared/fi-form-shared/fi-form-shared';
import { FiFormConditionHelperService } from '@fi-shared/fi-form-shared/fi-form-condition-helper.service';
import { isEqual } from '@fi-shared/utils';
import { FiComponentsContainer } from '@fi-shared/fi-components-container';
import { FiTooltipButtonIconColors } from '@fi-shared/fi-icon-tooltip-button/fi-icon-tooltip-button.component';
import { isNullOrUndefined } from 'util';
import { FiBooleanOperator } from '@fi-shared/types/fi-boolean-operator';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { AppTextService } from '@fi-shared/app-text';

@Component({
    selector: 'fi-form-logic-condition-if-is-container',
    template: `
        @if (isSimplePredicate(condition)) {
          <div
            class="if-s-predicate-container"
            [ngClass]="{ 'child-condition': isChildCondition() }"
            >
            <fi-form-logic-condition-if-is-predicate
              [condition]="condition"
              [rootCondition]="rootCondition"
              [parentCondition]="parentCondition"
              [formQuestions]="formQuestions"
              [isChildCondition]="isChildCondition()"
              [selectedLanguageId]="selectedLanguageId"
              >
            </fi-form-logic-condition-if-is-predicate>
            @if (isChildCondition()) {
              <fi-icon-tooltip-button
                [isIconVisible]="true"
                [iconClass]="'fa-trash'"
                [iconHoverColor]="FiTooltipButtonIconColors.Red"
                (onIconClick)="removeChildCondition()"
                >
              </fi-icon-tooltip-button>
            }
          </div>
        } @else {
          @for (childCondition of condition.ChildItems; track childCondition; let childConditionIndex = $index) {
            <fi-form-logic-condition-if-is-container
              [condition]="childCondition"
              [childConditionIndex]="childConditionIndex"
              [isChildConditionContainer]="true"
              [parentCondition]="condition"
              [rootCondition]="rootCondition"
              [formQuestions]="formQuestions"
              [selectedLanguageId]="selectedLanguageId"
              >
            </fi-form-logic-condition-if-is-container>
          }
        }
        `,
    styleUrls: ['./fi-form-logic-condition-if-is-container.component.scss'],
    providers: [
        {
            provide: FiComponentsContainer,
            useExisting: FiFormLogicConditionIfIsContainerComponent,
        },
    ],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-form-logic-condition-if-is-container' },
    standalone: false,
})
export class FiFormLogicConditionIfIsContainerComponent extends FiComponentsContainer {
    @Input() condition: IFormCondition;
    @Input() parentCondition: IFormCondition;
    @Input() rootCondition: IFormCondition;
    @Input() formQuestions: IFormQuestion[] = [];
    @Input() selectedLanguageId: number;
    @Input() childConditionIndex: number;
    @Input() isChildConditionContainer: boolean = false;

    FiTooltipButtonIconColors = FiTooltipButtonIconColors;

    constructor(
        private formConditionHelperService: FiFormConditionHelperService,
        private dialogsService: FiDialogsService,
        private appTextService: AppTextService,
        private cdr: ChangeDetectorRef,
    ) {
        super();
    }

    public isSimplePredicate(condition: IFormCondition) {
        return this.formConditionHelperService.isSimplePredicate(condition);
    }

    isChildCondition(): boolean {
        if (this.isSimplePredicate(this.condition))
            return !!this.childConditionIndex ? this.isChildConditionContainer : this.getParentConditionIndex() === 1;

        if (this.condition.ChildItems.length) return false;

        if (isEqual(this.parentCondition, this.rootCondition)) return !!this.childConditionIndex;

        if (this.getParentConditionIndex() === 0) return !!this.childConditionIndex;

        return true;
    }

    private getParentConditionIndex(): number {
        return this.rootCondition.ChildItems.findIndex((childItem: IFormCondition) => {
            return isEqual(childItem, this.parentCondition);
        });
    }

    removeChildCondition() {
        this.dialogsService
            .confirm({
                headerText: this.appTextService.get('form.condition_delete_child_header_text'),
                messageText: this.appTextService.get('form.condition_delete_child_message_text'),
                yesButtonText: this.appTextService.get('form.condition_delete_confirm'),
            })
            .onYesButtonClick(() => this.removeChildConditions());
    }

    private removeChildConditions(): void {
        const currentConditionIndex: number = this.getChildConditionIndex(this.parentCondition, this.condition);

        if (this.condition.ConditionOperator === FiBooleanOperator.And) {
            this.parentCondition = Object.assign(
                this.parentCondition,
                this.removeConditionFromParent(this.parentCondition, currentConditionIndex),
            );
            if (this.parentCondition.ChildItems.length === 1) {
                this.parentCondition = Object.assign({}, this.parentCondition.ChildItems[0]);
                this.parentCondition.ConditionOperator = FiBooleanOperator.Or;
            }
        } else {
            this.rootCondition = Object.assign(
                this.rootCondition,
                this.removeConditionFromParent(this.rootCondition, currentConditionIndex),
            );
        }

        if (this.rootCondition.ChildItems.length === 1) {
            this.rootCondition = Object.assign({}, this.rootCondition.ChildItems[0]);
        }

        const isChildrenNotDefinedOperator = this.rootCondition.ChildItems.every((childItem: IFormCondition) => {
            return isNullOrUndefined(childItem.ConditionOperator);
        });

        if (!this.rootCondition.ChildItems.length || isChildrenNotDefinedOperator)
            this.rootCondition.ConditionOperator = undefined;

        this.cdr.markForCheck();
    }

    private getChildConditionIndex(parentCondition: IFormCondition, condition: IFormCondition): number {
        return parentCondition.ChildItems.findIndex((childCondition: IFormCondition) => {
            return isEqual(childCondition, condition);
        });
    }

    private removeConditionFromParent(parentCondition: IFormCondition, conditionIndex: number): IFormCondition {
        parentCondition.ChildItems.splice(conditionIndex, 1);
        if (parentCondition.ChildItems.length === 1) {
            const tempActions = parentCondition.Actions;
            parentCondition = { ...parentCondition, ...parentCondition.ChildItems[0] };
            parentCondition.Actions = tempActions;
        }
        return parentCondition;
    }
}
